import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/helpers";
import DocumentList from "@/components/documents/DocumentList";
import { Scale, LogOut, Briefcase, CalendarClock, MessageSquare, Clock, Paperclip } from "lucide-react";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get("/portal/overview").then(({ data }) => setData(data)).catch((err) => {
      if (err?.response?.status === 402) setLocked(true);
    });
    api.get("/portal/documents").then(({ data }) => setDocuments(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-navy text-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gold text-navy"><Scale className="h-5 w-5" /></div>
          <div><p className="font-display text-xl leading-none">Advocate Diary</p><p className="text-xs text-white/50">Client Portal</p></div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-sm text-white/70 sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="portal-logout-btn" className="text-white hover:bg-white/10"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8" data-testid="client-portal-page">
        <h1 className="font-display text-4xl tracking-tight text-navy">Welcome, {user?.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Here's the latest on your legal matters.</p>

        {locked ? (
          <Card className="mt-8 border-gold/40 bg-gold/5 p-8 text-center" data-testid="portal-locked">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gold/20"><Scale className="h-6 w-6 text-gold-dark" /></div>
            <h2 className="mt-4 font-display text-2xl text-navy">Portal access unavailable</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">The client portal is not available on your firm's current plan. Please contact your advocate for updates on your matters.</p>
          </Card>
        ) : !data ? <LoadingState /> : (
          <div className="mt-8 space-y-8">
            <section>
              <div className="mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4 text-gold" /><h2 className="font-display text-2xl text-navy">My Cases</h2></div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {data.cases.length === 0 && <p className="text-sm text-muted-foreground">No cases on record.</p>}
                {data.cases.map((c) => (
                  <Card key={c.id} className="border-border p-5 shadow-sm">
                    <div className="flex items-start justify-between"><span className="text-xs text-muted-foreground">{c.case_number}</span><StatusBadge status={c.status} /></div>
                    <h3 className="mt-2 font-display text-xl text-navy">{c.title}</h3>
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <p>{c.court}</p>
                      <p className="text-navy">Next hearing: {fmtDate(c.next_hearing_date)}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-gold" /><h2 className="font-display text-2xl text-navy">Upcoming Hearings</h2></div>
              <Card className="divide-y divide-border border-border shadow-sm">
                {data.upcoming_hearings.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No upcoming hearings.</div>}
                {data.upcoming_hearings.map((h) => (
                  <div key={h.id} className="flex items-center gap-4 p-4">
                    <div className="flex w-16 shrink-0 flex-col items-center rounded-md bg-navy/5 py-1.5"><Clock className="h-3.5 w-3.5 text-navy" /><span className="mt-0.5 text-xs font-semibold text-navy">{h.time}</span></div>
                    <div className="min-w-0 flex-1"><p className="truncate font-medium text-navy">{h.case_title}</p><p className="truncate text-xs text-muted-foreground">{fmtDate(h.date)} · {h.court} · {h.purpose}</p></div>
                  </div>
                ))}
              </Card>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-gold" /><h2 className="font-display text-2xl text-navy">Case Updates</h2></div>
              <div className="space-y-3">
                {data.updates.length === 0 && <p className="text-sm text-muted-foreground">No updates shared with you yet.</p>}
                {data.updates.map((u) => (
                  <Card key={u.id} className="border-border p-4 shadow-sm">
                    <p className="text-xs font-medium text-gold">{u.case_title}</p>
                    <p className="mt-1 text-sm text-navy">{u.message}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{u.created_by} · {fmtDate(u.created_at)}</p>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2"><Paperclip className="h-4 w-4 text-gold" /><h2 className="font-display text-2xl text-navy">Shared Documents</h2></div>
              {documents.length === 0
                ? <p className="text-sm text-muted-foreground">No documents shared with you yet.</p>
                : <DocumentList documents={documents} readOnly showCase />}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
